import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Card, Stack, TextField, MenuItem,
    Button, LinearProgress, Typography, Alert, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    FormControlLabel, Checkbox, Tabs, Tab, Paper, InputAdornment,
    Tooltip, Fade
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Work as WorkIcon,
    Grade as GradeIcon,
    Class as ClassIcon,
    AssignmentInd as RoleIcon,
    Search as SearchIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const JobGradeSetup = ({ setIsLoggedIn }) => {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });
    const tenant = localStorage.getItem('tenant') || '';

    // Data States
    const [gradeSets, setGradeSets] = useState([]);
    const [grades, setGrades] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [roles, setRoles] = useState([]);

    // Dialog States
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            const [gsRes, gRes, jRes, rRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL1}/som/gradesets`, { withCredentials: true, headers }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/grades`, { withCredentials: true, headers }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/jobs`, { withCredentials: true, headers }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/roles`, { withCredentials: true, headers })
            ]);

            if (gsRes.data.success) setGradeSets(gsRes.data.data);
            if (gRes.data.success) setGrades(gRes.data.data);
            if (jRes.data.success) setJobs(jRes.data.data);
            if (rRes.data.success) setRoles(rRes.data.data);
        } catch (error) {
            console.error("Error fetching data:", error);
            setStatusMsg({ open: true, msg: 'Error fetching setup data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenDialog = useCallback((type, item = null) => {
        setDialogType(type);
        setEditingItem(item);
        if (item) {
            setFormData({ ...item });
        } else {
            const defaults = {
                gradeSets: { set_name: '', is_active: 1 },
                grades: { set_id: '', grade_code: '', grade_name: '', rank_level: 1, is_active: 1 },
                jobs: { job_code: '', job_title: '', is_active: 1 },
                roles: { job_id: '', role_name: '', is_active: 1 }
            };
            setFormData(defaults[type] || {});
        }
        setDialogOpen(true);
    }, []);

    const handleSave = useCallback(async () => {
        if (loading) return;
        
        // Basic Validation
        if (dialogType === 'gradeSets' && !formData.set_name) return setStatusMsg({ open: true, msg: 'Please provide a name', severity: 'warning' });
        if (dialogType === 'grades' && (!formData.grade_name || !formData.set_id)) return setStatusMsg({ open: true, msg: 'Please fill all required fields', severity: 'warning' });
        if (dialogType === 'jobs' && (!formData.job_title || !formData.job_code)) return setStatusMsg({ open: true, msg: 'Title and Code are required', severity: 'warning' });
        if (dialogType === 'roles' && (!formData.role_name || !formData.job_id)) return setStatusMsg({ open: true, msg: 'Select a Job Family and provide a title', severity: 'warning' });

        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            const endpoint = { gradeSets: 'gradesets', grades: 'grades', jobs: 'jobs', roles: 'roles' }[dialogType];
            const url = editingItem
                ? `${process.env.REACT_APP_API_URL1}/som/${endpoint}/${editingItem.id}`
                : `${process.env.REACT_APP_API_URL1}/som/${endpoint}`;

            const method = editingItem ? 'put' : 'post';
            const response = await axios[method](url, formData, { withCredentials: true, headers });

            if (response.data.success) {
                setStatusMsg({ open: true, msg: `${editingItem ? 'Updated' : 'Created'} successfully`, severity: 'success' });
                setDialogOpen(false);
                fetchData();
            }
        } catch (error) {
            console.error("Error saving:", error);
            setStatusMsg({ open: true, msg: error.response?.data?.message || 'Error saving data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [dialogType, editingItem, fetchData, formData, loading, tenant]);

    const handleDelete = useCallback(async (type, id) => {
        if (!window.confirm("Are you sure you want to delete this item? This may affect staff assignments.")) return;
        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            const endpoint = { gradeSets: 'gradesets', grades: 'grades', jobs: 'jobs', roles: 'roles' }[type];
            const response = await axios.delete(`${process.env.REACT_APP_API_URL1}/som/${endpoint}/${id}`, { withCredentials: true, headers });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Deleted successfully', severity: 'success' });
                fetchData();
            }
        } catch (error) {
            console.error("Error deleting:", error);
            setStatusMsg({ open: true, msg: 'Error deleting item', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [fetchData, tenant]);

    const commonColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        headerClass: 'premium-grid-header'
    }), []);

    const actionsRenderer = useCallback((type, params) => (
        <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
            <Tooltip title="Edit">
                <IconButton size="small" sx={{ color: 'primary.main' }} onClick={() => handleOpenDialog(type, params.data)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => handleDelete(type, params.data.id)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    ), [handleDelete, handleOpenDialog]);

    const columnDefs = useMemo(() => ({
        gradeSets: [
            { field: "set_name", headerName: "Grade Set Name", flex: 1, cellStyle: { fontWeight: 600, color: '#1e293b' } },
            { field: "is_active", headerName: "Status", width: 120, cellRenderer: p => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.value ? '#10b981' : '#94a3b8', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: p.value ? '#059669' : '#64748b', fontWeight: 600 }}>{p.value ? 'Active' : 'Inactive'}</Typography>
                </Box>
            )},
            { headerName: "Actions", width: 100, cellRenderer: p => actionsRenderer('gradeSets', p) }
        ],
        grades: [
            { field: "grade_code", headerName: "Code", width: 100, cellStyle: { fontWeight: 'bold', color: 'primary.main' } },
            { field: "grade_name", headerName: "Grade Name", flex: 1 },
            {
                headerName: "Grade Set",
                width: 180,
                valueGetter: p => gradeSets.find(gs => gs.id === p.data.set_id)?.set_name || 'N/A'
            },
            { field: "rank_level", headerName: "Rank", width: 90, type: 'numericColumn', cellStyle: { textAlign: 'center' } },
            { field: "is_active", headerName: "Status", width: 120, cellRenderer: p => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.value ? '#10b981' : '#94a3b8', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: p.value ? '#059669' : '#64748b', fontWeight: 600 }}>{p.value ? 'Active' : 'Inactive'}</Typography>
                </Box>
            )},
            { headerName: "Actions", width: 100, cellRenderer: p => actionsRenderer('grades', p) }
        ],
        jobs: [
            { field: "job_code", headerName: "Family Code", width: 120, cellStyle: { color: 'primary.main' } },
            { field: "job_title", headerName: "Job Family", flex: 1, cellStyle: { fontWeight: 600 } },
            { field: "is_active", headerName: "Status", width: 120, cellRenderer: p => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.value ? '#10b981' : '#94a3b8', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: p.value ? '#059669' : '#64748b', fontWeight: 600 }}>{p.value ? 'Active' : 'Inactive'}</Typography>
                </Box>
            )},
            { headerName: "Actions", width: 100, cellRenderer: p => actionsRenderer('jobs', p) }
        ],
        roles: [
            { field: "role_name", headerName: "Job Title", flex: 1, cellStyle: { fontWeight: 600 } },
            {
                headerName: "Job Family",
                width: 250,
                valueGetter: p => jobs.find(j => j.id === p.data.job_id)?.job_title || 'N/A'
            },
            { field: "is_active", headerName: "Status", width: 120, cellRenderer: p => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.value ? '#10b981' : '#94a3b8', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: p.value ? '#059669' : '#64748b', fontWeight: 600 }}>{p.value ? 'Active' : 'Inactive'}</Typography>
                </Box>
            )},
            { headerName: "Actions", width: 100, cellRenderer: p => actionsRenderer('roles', p) }
        ]
    }), [gradeSets, jobs, actionsRenderer]);

    const activeRows = useMemo(() => {
        const dataArr = [gradeSets, grades, jobs, roles][tabValue] || [];
        if (!searchText) return dataArr;
        const s = searchText.toLowerCase();
        return dataArr.filter(item => 
            Object.values(item).some(val => String(val).toLowerCase().includes(s))
        );
    }, [tabValue, gradeSets, grades, jobs, roles, searchText]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader title="Job & Grade Setup" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 4, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1000, mx: 'auto', width: '100%' }}>
                <Fade in timeout={500}>
                    <Card sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', bgcolor: '#fff' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                    Job Catalogue & Grade Hierarchy
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                    Define organizational structures, job families, and compensation levels.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1.5}>
                                <TextField
                                    size="small"
                                    placeholder="Quick search..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: 2, bgcolor: '#f1f5f9', border: 'none', '& fieldset': { border: 'none' } }
                                    }}
                                />
                                <Tooltip title="Refresh Data">
                                    <IconButton onClick={fetchData} sx={{ bgcolor: '#f1f5f9' }}><RefreshIcon /></IconButton>
                                </Tooltip>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    sx={{ 
                                        bgcolor: 'primary.main', 
                                        borderRadius: 2, 
                                        px: 3, 
                                        textTransform: 'none', 
                                        fontWeight: 700,
                                        boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                                        '&:hover': { bgcolor: 'primary.dark' }
                                    }}
                                    onClick={() => handleOpenDialog(['gradeSets', 'grades', 'jobs', 'roles'][tabValue])}
                                >
                                    Add New
                                </Button>
                            </Stack>
                        </Stack>
                    </Card>
                </Fade>

                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => setTabValue(v)}
                        centered
                        sx={{
                            bgcolor: '#fff',
                            borderBottom: '1px solid #f1f5f9',
                            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: 'primary.main' },
                            '& .MuiTab-root': { py: 2, fontWeight: 700, color: '#64748b', textTransform: 'none', fontSize: 15, minWidth: 150 },
                            '& .Mui-selected': { color: '#6366f1 !important' }
                        }}
                    >
                        <Tab icon={<ClassIcon />} iconPosition="start" label="Grade Sets" />
                        <Tab icon={<GradeIcon />} iconPosition="start" label="Grades" />
                        <Tab icon={<WorkIcon />} iconPosition="start" label="Job Families" />
                        <Tab icon={<RoleIcon />} iconPosition="start" label="Job Titles" />
                    </Tabs>
                    
                    <Box sx={{ height: 'calc(100vh - 380px)', p: 1, bgcolor: '#fff' }}>
                        {loading && <LinearProgress sx={{ borderRadius: 1, mb: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} />}
                        <Box className="ag-theme-quartz" sx={{ height: '100%', width: '100%', '& .premium-grid-header': { fontWeight: 800, color: '#475569' } }}>
                            <AgGridReact
                                rowData={activeRows}
                                columnDefs={columnDefs[['gradeSets', 'grades', 'jobs', 'roles'][tabValue]]}
                                defaultColDef={commonColDef}
                                rowHeight={52}
                                animateRows={true}
                                pagination={true}
                                paginationPageSize={20}
                                headerHeight={48}
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <Dialog 
                open={dialogOpen} 
                onClose={() => setDialogOpen(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#0f172a' }}>
                    {editingItem ? 'Update' : 'Create New'} {
                        dialogType === 'gradeSets' ? 'Grade Set' :
                        dialogType === 'grades' ? 'Grade Level' :
                        dialogType === 'jobs' ? 'Job Family' :
                        dialogType === 'roles' ? 'Job Title' : 'Record'
                    }
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
                        {dialogType === 'gradeSets' && (
                            <TextField
                                fullWidth label="Grade Set Name" size="small"
                                value={formData.set_name || ''}
                                onChange={e => setFormData({ ...formData, set_name: e.target.value })}
                                placeholder="e.g. Executive Pay Scale"
                            />
                        )}

                        {dialogType === 'grades' && (
                            <>
                                <TextField
                                    select fullWidth label="Grade Set Assignment" size="small"
                                    value={formData.set_id || ''}
                                    onChange={e => setFormData({ ...formData, set_id: e.target.value })}
                                >
                                    {gradeSets.map(gs => <MenuItem key={gs.id} value={gs.id}>{gs.set_name}</MenuItem>)}
                                </TextField>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth label="Code" size="small"
                                        value={formData.grade_code || ''}
                                        onChange={e => setFormData({ ...formData, grade_code: e.target.value.toUpperCase() })}
                                        placeholder="e.g. G1"
                                    />
                                    <TextField
                                        fullWidth label="Grade Name" size="small"
                                        value={formData.grade_name || ''}
                                        onChange={e => setFormData({ ...formData, grade_name: e.target.value })}
                                        placeholder="e.g. Senior Associate"
                                    />
                                </Box>
                                <TextField
                                    fullWidth label="Hierarchy Rank" type="number" size="small"
                                    value={formData.rank_level || 1}
                                    onChange={e => setFormData({ ...formData, rank_level: e.target.value })}
                                    helperText="Determines relative authority (Lower = Higher Rank)"
                                />
                            </>
                        )}

                        {dialogType === 'jobs' && (
                            <>
                                <TextField
                                    fullWidth label="Family Code" size="small"
                                    value={formData.job_code || ''}
                                    onChange={e => setFormData({ ...formData, job_code: e.target.value.toUpperCase() })}
                                    placeholder="e.g. IT"
                                />
                                <TextField
                                    fullWidth label="Job Family Name" size="small"
                                    value={formData.job_title || ''}
                                    onChange={e => setFormData({ ...formData, job_title: e.target.value })}
                                    placeholder="e.g. Information Technology"
                                />
                            </>
                        )}

                        {dialogType === 'roles' && (
                            <>
                                <TextField
                                    select fullWidth label="Parent Job Family" size="small"
                                    value={formData.job_id || ''}
                                    onChange={e => setFormData({ ...formData, job_id: e.target.value })}
                                >
                                    {jobs.map(j => <MenuItem key={j.id} value={j.id}>{j.job_title}</MenuItem>)}
                                </TextField>
                                <TextField
                                    fullWidth label="Official Job Title" size="small"
                                    value={formData.role_name || ''}
                                    onChange={e => setFormData({ ...formData, role_name: e.target.value })}
                                    placeholder="e.g. Senior Software Engineer"
                                />
                            </>
                        )}

                        <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={formData.is_active === 1} 
                                        onChange={e => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                                        sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                                    />
                                }
                                label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>Active & Visible in Assignments</Typography>}
                                sx={{ ml: 0 }}
                            />
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                    <Button 
                        onClick={handleSave} 
                        variant="contained" 
                        sx={{ 
                            bgcolor: 'primary.main', 
                            borderRadius: 2, 
                            px: 3, 
                            fontWeight: 700, 
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        {editingItem ? 'Confirm Update' : 'Create Record'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={4000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
                TransitionComponent={Fade}
            >
                <Alert 
                    severity={statusMsg.severity} 
                    variant="filled" 
                    sx={{ width: '100%', borderRadius: 3, fontWeight: 600, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                >
                    {statusMsg.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default JobGradeSetup;
